import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Sidebar Component
 * Left navigation sidebar matching the ZrpGlobal design
 * Responsive: Full sidebar on desktop, drawer on mobile/tablet
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useMobileNav } from '../context/MobileNavContext';
import { 
  Home, 
  Wallet, 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  Calendar, 
  HelpCircle, 
  Settings, 
  Upload, 
  Sun, 
  Moon, 
  Compass, 
  LogOut,
  Share2,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { WebteraLogo } from '../../components/WebteraLogo';

const TraderSidebar = () => {
  const tx = useTraderText();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useTraderAuth();
  const { isDark, toggleTheme } = useTraderTheme();
  const { t } = useTraderLanguage();
  const { sidebarOpen, setSidebarOpen, isMobile, isTablet } = useMobileNav();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const menuItems = [
    { icon: Home, label: t('nav.home'), path: '/trader', id: 'home' },
    { icon: Wallet, label: t('nav.funds'), path: '/trader/funds', id: 'funds' },
    { icon: ArrowDownToLine, label: t('nav.deposit'), path: '/trader/deposit', id: 'deposit' },
    { icon: ArrowUpFromLine, label: t('nav.withdraw'), path: '/trader/withdraw', id: 'withdraw' },
    { icon: Calendar, label: t('nav.economicCalendar'), path: '/trader/calendar', id: 'calendar' },
    { icon: HelpCircle, label: t('nav.helpCenter'), path: '/trader/help', id: 'help' },
    { icon: Settings, label: t('nav.personalSettings'), path: '/trader/settings', id: 'settings' },
    { icon: Upload, label: t('nav.uploadDocuments'), path: '/trader/documents', id: 'documents' },
  ];

  const bottomItems = [
    { 
      icon: isDark ? Sun : Moon, 
      label: isDark ? t('nav.lightMode') : t('nav.darkMode'), 
      action: toggleTheme, 
      id: 'theme' 
    },
    { icon: Compass, label: t('nav.tourGuide'), action: () => {}, id: 'tour' },
    { icon: Share2, label: t('nav.shareApp'), action: () => {}, id: 'share' },
    { icon: LogOut, label: t('nav.logOut'), action: logout, id: 'logout', danger: true },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  };

  const handleActionClick = (action) => {
    action();
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  };

  // Determine width based on device and collapsed state
  const sidebarWidth = isMobile ? 'w-72' : isCollapsed ? 'w-16' : 'w-56';

  // Mobile/Tablet: Drawer overlay
  if (isMobile || isTablet) {
    return (
      <>
        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Drawer */}
        <div 
          className={`fixed top-0 left-0 h-full z-50 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } ${sidebarWidth} ${isDark ? 'bg-[#0f1724]' : 'bg-white'}`}
        >
          {/* Close button */}
          <div className={`p-4 flex items-center justify-between border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <WebteraLogo size={26} isDark={isDark} subtitle="TRADER" />
            <button
              onClick={() => setSidebarOpen(false)}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <X size={20} />
            </button>
          </div>

          {/* Menu items */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                    active 
                      ? isDark 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : 'bg-blue-50 text-blue-600'
                      : isDark
                        ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon size={20} />
                  <span className="text-sm font-medium">{tx(item.label)}</span>
                </button>
              );
            })}
          </nav>

          {/* Bottom items */}
          <div className={`px-2 py-4 space-y-1 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            {bottomItems.map((item) => {
              const Icon = item.icon;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleActionClick(item.action)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                    item.danger
                      ? 'text-red-400 hover:bg-red-500/10'
                      : isDark
                        ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon size={20} />
                  <span className="text-sm font-medium">{tx(item.label)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  // Desktop: Fixed sidebar
  return (
    <div 
      className={`h-full flex flex-col transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-56'
      } ${isDark ? 'bg-[#0f1724] border-r border-gray-800' : 'bg-white border-r border-gray-200'}`}
    >
      {/* Logo Area */}
      <div className={`p-4 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {isCollapsed ? (
          <WebteraLogo compact size={26} />
        ) : (
          <WebteraLogo size={26} isDark={isDark} subtitle="TRADER" />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`p-1.5 rounded-lg transition-colors ${
            isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
          }`}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Main Menu */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                isCollapsed ? 'justify-center' : ''
              } ${
                active 
                  ? isDark 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'bg-blue-50 text-blue-600'
                  : isDark
                    ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              data-testid={`sidebar-${item.id}`}
              title={isCollapsed ? item.label : undefined}
            >
              <Icon size={20} />
              {!isCollapsed && <span className="text-sm font-medium">{tx(item.label)}</span>}
            </button>
          );
        })}
      </nav>

      {/* Bottom Menu */}
      <div className={`px-2 py-4 space-y-1 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        {bottomItems.map((item) => {
          const Icon = item.icon;
          
          return (
            <button
              key={item.id}
              onClick={item.action}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                isCollapsed ? 'justify-center' : ''
              } ${
                item.danger
                  ? 'text-red-400 hover:bg-red-500/10'
                  : isDark
                    ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              data-testid={`sidebar-${item.id}`}
              title={isCollapsed ? item.label : undefined}
            >
              <Icon size={20} />
              {!isCollapsed && <span className="text-sm font-medium">{tx(item.label)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TraderSidebar;
